import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getNetworkAvailable } from '../../selectors/config'
import { createOrder } from '../../VNPrinters/Apis'
import { ToastManager } from '../../utils/toastManager'
import { saveOrder, updateOrderInProgress } from '../../actions/order'
import { getActiveAttendantId } from '../../selectors/attendant'
import { getOrderInProgress } from '../../selectors/order'
import StadiumRemote from '../../remote/stadiumRemote'
import { setReopenedTabPayments } from '../../VNTenders/ActionCreators'
import { pluckAuthPayment } from '../../VNTenders/hooks/useAuths'

export const useSyncOrders = () => {
  const dispatch = useDispatch()
  const networkConnected = useSelector(getNetworkAvailable)
  const employeeId = useSelector(getActiveAttendantId)
  const order = useSelector(getOrderInProgress)

  /**
   * Creates then fetches tab order if it was not created in Stadium
   */
  const syncOrderWithAuthIfNeeded = useCallback(
    async (stashAuthPaymentForLaterUse = false, containTipAndCharity = true) => {
      if (!networkConnected) return false
      if (order?.wasCreatedInStadium) return false
      if (!order?.isTab) return false

      const taggedPayments = order.payments?.map((payment) => ({
        ...payment,
        userAttributes: { ...payment.userAttributes, isOriginalAuth: true },
      }))
      const payload = { ...order, payments: taggedPayments, employeeId }
      const createOrderPayload = { ...payload }
      if (!containTipAndCharity) {
        delete createOrderPayload.tipAmountInCents
        delete createOrderPayload.charityAmountInCents
      }

      const result = await createOrder(createOrderPayload)

      if (!result) {
        ToastManager.error('Failed to sync order', { autoClose: false })
        return false
      }

      const updatedOrderData = await StadiumRemote.getOrderDetails(order.uuid)
      const total = updatedOrderData.data.total
      const amountInCents = Math.round(parseFloat(total) * 100)
      const taxAmountInCents = updatedOrderData.data.lineItems?.reduce?.((acc, item) => {
        if (item.itemSpecialType === 'tax') {
          acc += parseFloat(item.price) * 100
        }
        return acc
      }, 0)

      // when tab order is first created, it can only be a single credit card auth
      const curPayments = updatedOrderData.data.paymentInfo?.map?.((payment) => ({
        ...payment,
        originalAuthId: payment.uuid,
        paymentId: payment.uuid,
        userAttributes: {
          ...payment.userAttributes,
          isOriginalAuth: true,
          paymentId: payment.uuid,
        },
      }))

      // map payments from Stadium paymentInfo to local payments
      const updatedOrder = {
        ...payload,
        payments: curPayments,
        amountInCents,
        balanceDueInCents: amountInCents,
        taxAmountInCents,
        total,
        wasCreatedInStadium: true,
      }

      if (stashAuthPaymentForLaterUse) {
        const { auth, payments } = pluckAuthPayment(updatedOrder)

        const orderWithStashedAuth = {
          ...updatedOrder,
          payments,
          originalTransactionId: order.cardReaderData?.uuid,
          neededToBeVoidedAuthPayment: [auth],
        }
        dispatch(setReopenedTabPayments({ orderUuid: order?.uuid, payments: [auth] }))
        dispatch(updateOrderInProgress(orderWithStashedAuth))
        dispatch(saveOrder(orderWithStashedAuth))

        return
      }

      dispatch(saveOrder(updatedOrder))
      dispatch(updateOrderInProgress(updatedOrder))
      return true
    },
    [networkConnected, order, employeeId, dispatch],
  )

  return { syncOrderWithAuthIfNeeded }
}
